import { useTranslation } from 'react-i18next';
import { Kpi, type KpiTone } from '../components/Kpi';
import { EmptyState } from '../components/EmptyState';
import { useRole } from '../auth/useRole';
import { useTenantContext } from '../auth/useTenantContext';
import { usePickListCounts } from '../api/pickLists';
import { LiveMonitoringPanel } from '../components/live/LiveMonitoringPanel';

export default function Home() {
  const { role } = useRole();

  if (role === 'platform-admin') {
    return (
      <EmptyState
        icon={<i className="bi bi-buildings" />}
        title="Cruscotto piattaforma"
        body="Gestione tenants, schemi item, dialog templates e audit cross-tenant arrivano nel sotto-progetto SP-5b/d."
      />
    );
  }

  // voice-supervisor: solo monitoraggio live (read-only, niente KPI pick-list).
  if (role === 'voice-supervisor') {
    return (
      <div>
        <LiveSectionHeading />
        <LiveMonitoringPanel />
      </div>
    );
  }

  // tenant-admin / warehouse-admin: KPI in cima + monitoraggio live sotto.
  // La pagina "Stato attuale" non è più una voce di menu a sé: il suo
  // contenuto è confluito qui (la route /live resta raggiungibile via URL).
  return (
    <div>
      <DashboardKpis />
      <LiveSectionHeading />
      <LiveMonitoringPanel />
    </div>
  );
}

/** Titoletto della sezione live dentro la Dashboard. */
function LiveSectionHeading() {
  return (
    <h2 className="vp-label d-flex align-items-center gap-2 mb-3">
      <i className="bi bi-activity" />
      Stato attuale
    </h2>
  );
}

/**
 * dashboard-kpi — KPI card cablate all'endpoint /admin/pick-lists/counts.
 *
 * Una card per OGNI stato del filtro pick-list (Ricevuta · Assegnata ·
 * In prelievo · Evasa · Confermata · Annullata · Archiviata) + la card
 * derivata "Evase parzialmente". Ogni card:
 *  - usa il colore (tone) del badge di stato corrispondente
 *    (vedi PickListStatusBadge.TONE_BY_STATUS);
 *  - linka alla pagina Pick-list con il filtro status preconfigurato via
 *    query string (deep-link letto da PickListsList al mount).
 */
type StatusKey = 'open' | 'assigned' | 'in_progress' | 'fulfilled' | 'confirmed' | 'cancelled' | 'archived';

// Tone allineato a PickListStatusBadge (stesso colore delle label di stato).
const STATUS_CARDS: { key: StatusKey; tone: KpiTone; icon: string; status: string }[] = [
  { key: 'open',        tone: 'neutral', icon: 'bi-inbox',           status: 'open' },
  { key: 'assigned',    tone: 'primary', icon: 'bi-person-check',    status: 'assigned' },
  { key: 'in_progress', tone: 'warn',    icon: 'bi-hourglass-split', status: 'in_progress' },
  { key: 'fulfilled',   tone: 'success', icon: 'bi-box-seam',        status: 'fulfilled' },
  { key: 'confirmed',   tone: 'primary', icon: 'bi-check2-circle',   status: 'confirmed' },
  { key: 'cancelled',   tone: 'danger',  icon: 'bi-x-circle',        status: 'cancelled' },
  { key: 'archived',    tone: 'neutral', icon: 'bi-archive',         status: 'archived' },
];

function DashboardKpis() {
  const { t } = useTranslation('common');
  const { token, tenantCode, warehouseId } = useTenantContext();
  const countsQ = usePickListCounts(token, tenantCode, warehouseId);
  const c = countsQ.data?.by_status;
  const loading = countsQ.isLoading;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16, marginBottom: 24 }}>
        {STATUS_CARDS.map((card) => (
          <Kpi
            key={card.key}
            label={t(`status.${card.key}`)}
            value={loading || c === undefined ? '—' : c[card.key]}
            icon={card.icon}
            iconTone={card.tone}
            tone={card.tone}
            href={`/pick-lists?status=${card.status}`}
          />
        ))}
        <Kpi
          label="In attesa di colli"
          value={loading || c === undefined ? '—' : c.awaiting_colli}
          icon="bi-box-seam"
          iconTone="warn"
          tone="warn"
          hint="Ferme: non vengono inviate a Book"
          href="/pick-lists?awaiting_colli=true"
        />
        <Kpi
          label="Evase parzialmente"
          value={loading || c === undefined ? '—' : c.with_partials}
          icon="bi-exclamation-triangle"
          iconTone="warn"
          tone="warn"
          hint="Almeno una riga inevasa"
          href="/pick-lists?partials=true"
        />
      </div>
      {countsQ.isError && (
        <p className="text-muted small">
          Impossibile caricare i conteggi delle pick-list. Riprova o aggiorna la pagina.
        </p>
      )}
    </div>
  );
}
